// Removing licences (anima_core #8, 08.10.2026). "Entfernen" archives: the
// hub keeps the row, hides it from the list unless asked, and activate and
// verify no longer know it. Admin only, as on the hub. Pure, no imports: the
// node tests load this file directly (licence_rows.test.ts).
//
// anima_core #7 (03.11.2026): only unused licences may go - never taken by a
// machine, or no contact for licenseUnusedDays days; the hub decides and sends
// it as "unused". Several can be removed at once (POST api/License/archive).

/** What these functions read of a licence row. */
export interface LicenceRow {
    uuid: string;
    archived?: boolean | null;
    unused?: boolean | null;
    foreignKeyFingerprint?: string | null;
    foreignKeySeen?: string | null;
}

/** Whether the licence is archived; an older hub sends no field - not archived. */
export const isArchived = (row: LicenceRow): boolean => row.archived === true;

/** Whether the hub calls the licence unused; an older hub sends no field - in use, nothing offered. */
export const isUnused = (row: LicenceRow): boolean => row.unused === true;

/**
 * Core#6 (03.11.2026): the foreign licence key a spoke of this licence
 * reported, or null - the spoke checks its licence with a key that is not the
 * hub's, i.e. runs on licences the hub did not issue. An older hub sends no
 * field: nothing to show.
 */
export const foreignKey = (row: LicenceRow): string | null => {
    const fingerprint = (row.foreignKeyFingerprint ?? "").trim();
    return fingerprint.length > 0 ? fingerprint : null;
};

/** The tooltip line under the "fremder Schluessel" marker: fingerprint and, when known, when seen. */
export const foreignKeyDetail = (row: LicenceRow): string | null => {
    const fingerprint = foreignKey(row);
    if (fingerprint === null) {
        return null;
    }
    const seen = (row.foreignKeySeen ?? "").trim();
    return seen.length > 0 ? `${fingerprint} (${seen})` : fingerprint;
};

/** Whether the "ungenutzt" marker is shown: unused and not removed yet. */
export const showsUnused = (row: LicenceRow): boolean => isUnused(row) && !isArchived(row);

/** Whether the "Entfernen" button is offered: admins, for an unused licence not removed yet. */
export const mayRemove = (row: LicenceRow, isAdmin: boolean): boolean => isAdmin && showsUnused(row);

/** Whether "Wiederherstellen" is offered: admins, for a removed licence. */
export const mayRestore = (row: LicenceRow, isAdmin: boolean): boolean => isAdmin && isArchived(row);

/**
 * The selected licences that may be removed, in list order, each once.
 * Rows that left the list or may not go any more drop out.
 */
export const removableSelection = (
    rows: readonly LicenceRow[],
    selected: readonly string[],
    isAdmin: boolean,
): string[] =>
    rows.filter((row) => selected.includes(row.uuid) && mayRemove(row, isAdmin)).map((row) => row.uuid);

/** One licence in the hub's answer to a bulk removal. */
export interface BulkResult {
    uuid: string;
    outcome: string;
    message?: string;
}

/** How a bulk removal went: archived, already removed, refused (in use or unknown). */
export interface BulkSummary {
    archived: number;
    unchanged: number;
    refused: number;
}

/** Counts the hub's per-licence outcomes; anything it does not name archived or unchanged counts as refused. */
export const summariseBulk = (results: readonly BulkResult[] | null | undefined): BulkSummary =>
    (results ?? []).reduce<BulkSummary>(
        (sum, r) =>
            r.outcome === "archived" ? { ...sum, archived: sum.archived + 1 }
            : r.outcome === "unchanged" ? { ...sum, unchanged: sum.unchanged + 1 }
            : { ...sum, refused: sum.refused + 1 },
        { archived: 0, unchanged: 0, refused: 0 },
    );
